import React, { useState, useMemo } from "react";
import { Box, Stack, useMediaQuery, useTheme, Paper, Button } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import PageHeader from "../../components/page-layout/PageHeader";
import MobileHeader from "../../components/layout/MobileHeader";
import AllTransactionsSummary from "../../components/all-transactions/AllTransactionsSummary";
import AllTransactionsFilters from "../../components/all-transactions/AllTransactionsFilters";
import AllTransactionsTable from "../../components/all-transactions/AllTransactionsTable";
import { mockTransactionsData } from "../../mocks/transactionsMockData";

const AllTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();

  const selectedDate = location.state?.date;

  const [searchText, setSearchText] = useState("");
  const [searchFilter, setSearchFilter] = useState("transactionId");
  const [statusFilter, setStatusFilter] = useState("");

  // ✅ Get with safety check
  const allTransactions = useMemo(() => {
    const transactions = mockTransactionsData?.detailedTransactions || [];

    // If no specific date, return all transactions
    if (!selectedDate) {
      return transactions;
    }

    // Filter by selected date
    return transactions.filter((txn) => txn.date === selectedDate);
  }, [selectedDate]);

  // ✅ Filter transactions with safety check
  const filteredTransactions = useMemo(() => {
    if (!allTransactions || allTransactions.length === 0) {
      return [];
    }

    let result = [...allTransactions];

    // Filter by status
    if (statusFilter && statusFilter !== "ALL") {
      result = result.filter((txn) => txn.status === statusFilter);
    }

    // Filter by search based on selected filter
    if (searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase().trim();
      result = result.filter((txn) => {
        if (searchFilter === "transactionId") {
          return txn.transactionId?.toLowerCase().includes(searchLower);
        } else if (searchFilter === "userId") {
          return txn.userId?.toLowerCase().includes(searchLower);
        }
        return false;
      });
    }

    return result;
  }, [allTransactions, searchText, statusFilter, searchFilter]);

  // ✅ Calculate summary stats with safety check
  const summaryStats = useMemo(() => {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      return {
        totalTransactions: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        totalEarnings: 0,
      };
    }

    const totalTransactions = filteredTransactions.length;
    const successfulTransactions = filteredTransactions.filter(
      (txn) => txn.status === "COMPLETED"
    ).length;
    const failedTransactions = filteredTransactions.filter(
      (txn) => txn.status === "FAILED"
    ).length;
    const totalEarnings = filteredTransactions
      .filter((txn) => txn.status === "COMPLETED")
      .reduce((sum, txn) => sum + (txn.netEarning || 0), 0);

    return {
      totalTransactions,
      successfulTransactions,
      failedTransactions,
      totalEarnings,
    };
  }, [filteredTransactions]);

  const handleGetInvoice = () => {
    navigate("/transaction-report", {
      state: { transactions: filteredTransactions, date: selectedDate },
    });
  };

  return (
    <Stack sx={{ px: isMobile ? 1 : 0 }}>
      {/* Header with GET INVOICE button */}
      {isMobile ? (
        <MobileHeader
          title={
            selectedDate ? `Transactions - ${selectedDate}` : "All Transactions"
          }
          showBack
        />
      ) : (
        <PageHeader
          showBack
          title={
            selectedDate ? `Transactions - ${selectedDate}` : "All Transactions"
          }
          lead={
            selectedDate
              ? `Detailed transaction list for ${selectedDate}`
              : "View all your transactions"
          }
          rightComponent={
            <Button
              variant="outlined"
              size="small"
              onClick={handleGetInvoice}
              sx={{ fontSize: "0.75rem", py: 0.5 }}
            >
              Get Invoice
            </Button>
          }
        />
      )}

      {/* Main Content */}
      <Box
        sx={{
          px: isMobile ? 0 : 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Summary Cards */}
        <AllTransactionsSummary stats={summaryStats} />

        {/* Filters */}
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <AllTransactionsFilters
            searchText={searchText}
            onSearchChange={setSearchText}
            searchFilter={searchFilter}
            onSearchFilterChange={setSearchFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            isMobile={isMobile}
          />
        </Paper>

        {/* Table - ✅ Ensure filteredTransactions is always an array */}
        <AllTransactionsTable
          transactions={filteredTransactions || []}
          isMobile={isMobile}
        />
      </Box>
    </Stack>
  );
};

export default AllTransactions;
